import type { WalletDto } from "@umi/api/schemas/wallet";
import { catchBlock } from "@umi/shared/catchBlock";
import { EVM_CHAINS, getChain, getChainManager, type ChainArch, type ChainTag, type EvmChainTag } from "@umi/shared/chains";
import { UnknownTokenStandardError } from "@umi/shared/errors";
import { EvmManager } from "@umi/shared/managers/evm";
import type { HydratedWallet } from "@umi/shared/types/wallet";
import { createWallet } from "@/utils/wallets";
import _ from "lodash";
import type { Address } from "viem";


/**
 * Return the decrypted pks for a list of wallets.
 * @param wallets
 * @returns
 */
export const decryptPks = async (wallets: HydratedWallet[]) => {
  const [clearPks, err] = await catchBlock(() => {
    return Promise.all(wallets.map(async (wallet) => ({
      walletAddress: wallet.address,
      pk: await decrypt(wallet.encryptedPk!, wallet.address)
    }))).then((res) => _.keyBy(res, "walletAddress"));
  });

  if (err) {
    showErrorNotification(`Could not decrypt private keys. Open a support ticket.`);
    throw err;
  }

  return clearPks;
};


/**
 * Create new wallets using the wallet worker and return them.
 * @param args
 */
export const createWalletsWithWorker = async (chainArch: ChainArch, count: number, tags?: string[]) => {
  const pks = await createPksWorker(chainArch, count);
  const addDate = new Date();
  return Promise.all(pks.map(pk => createWallet(
    {
      pk,
      chainArch,
      tags,
      addedAt: addDate
    },
    (pk) => encrypt(pk)
  )));
};


/**
 * Create new wallets and return only their address.
 * Used for the demo on the landing page.
 * @param args
 */
export const createDemoWallet = async () => {
  const demoPk = EvmManager.createPk();
  return EvmManager.toAddress(demoPk);
};


type BalanceWaiter = {
  address: string;
  resolve: (balance: bigint | bigint[]) => void;
  reject: (error: unknown) => void;
};

export const balanceQueue = new Map<string, Set<string>>();
// Multiple refreshBalance calls for the same wallet can race; keep every waiter
// so none of them hang if a later call overwrites the map entry.
export const balanceResolvers = new Map<string, Omit<BalanceWaiter, "address">[]>();

// Covers chunked ERC721 Lens reads across large wallet sets without leaving
// the disperse token spinner stuck when an RPC call never settles.
const BALANCE_FETCH_TIMEOUT_MS = 10_000;

// Upper bound on how long a poll may block the next one. Comfortably above
// BALANCE_FETCH_TIMEOUT_MS so only a genuinely stuck fetch trips it.
const STALE_FETCH_MS = 30_000;

/**
 * Take ownership of waiters for this batch up front. A timed-out or late RPC
 * must only settle these waiters — never ones queued by a later refresh.
 */
const claimBalanceWaiters = (
  addresses: string[],
  chainTag: ChainTag,
  tokenAddress: string,
): BalanceWaiter[] => {
  const claimed: BalanceWaiter[] = [];
  for (const address of addresses) {
    const key = `${address}:${chainTag}:${tokenAddress}`;
    const waiters = balanceResolvers.get(key);
    if (!waiters?.length) continue;
    balanceResolvers.delete(key);
    for (const waiter of waiters) {
      claimed.push({ address, ...waiter });
    }
  }
  return claimed;
};

const settleClaimedWaiters = (
  waiters: BalanceWaiter[],
  result: { ok: true, balances: Record<string, bigint | bigint[] | readonly bigint[]> } | { ok: false, error: unknown }
) => {
  for (const waiter of waiters) {
    if (result.ok) {
      if (!Object.prototype.hasOwnProperty.call(result.balances, waiter.address)) {
        waiter.reject(new Error(`Missing balance for address ${waiter.address}`));
        continue;
      }
      // hasOwnProperty does not narrow Record indexing; value is present here
      waiter.resolve(result.balances[waiter.address] as bigint | bigint[]);
    } else {
      waiter.reject(result.error);
    }
  }
};


/**
 * Add a wallet address to the balance queue for a specific chain and token.
 * Will then be picked up by @see processBalanceQueue after debounce.
 * @param chainTag
 * @param tokenAddress
 * @param walletAddress
 * @param waiter
 */
export const queueBalance = (
  chainTag: ChainTag,
  tokenAddress: string,
  walletAddress: string,
  waiter: Omit<BalanceWaiter, "address">
) => {
  const key = `${chainTag}:${tokenAddress}`;
  if (!balanceQueue.has(key)) balanceQueue.set(key, new Set());
  balanceQueue.get(key)!.add(walletAddress);

  const waiterKey = `${walletAddress}:${chainTag}:${tokenAddress}`;
  const waiters = balanceResolvers.get(waiterKey) ?? [];
  waiters.push(waiter);
  balanceResolvers.set(waiterKey, waiters);
};


/**
 * Process the balance queue for a specific chain tag.
 * Always settles every waiter claimed from the queue — a missing token or RPC
 * failure used to leave refreshBalance promises pending forever, which stuck
 * disperse loadToken on the token search spinner.
 */
export const processBalanceQueue = _.debounce(async () => {

  const walletStore = useWalletStore();

  const entries = Array.from(balanceQueue.entries());
  balanceQueue.clear();

  await Promise.allSettled(entries.map(async ([key, addressesSet]) => {

    const [chainTag, tokenAddress] = key.split(":") as [ChainTag, string];
    const addresses = Array.from(addressesSet);
    // Claim before awaiting so a late response cannot settle a newer refresh.
    const waiters = claimBalanceWaiters(addresses, chainTag, tokenAddress);
    const token = walletStore.token(chainTag, tokenAddress);

    if (!token) {
      const error = new Error(`No token found for chain ${chainTag} and address ${tokenAddress}`);
      console.warn(error.message);
      settleClaimedWaiters(waiters, { ok: false, error });
      return;
    }

    try {
      const evm = getChainManager(chainTag as EvmChainTag);
      const fetch = (() => {
        if (token.standard === "native") return evm.getBalances(addresses as Address[]);
        if (token.standard === "erc20") return evm.getErc20Balances(tokenAddress as Address, addresses as Address[]);
        if (token.standard === "erc1155") return evm.getErc1155Balances(tokenAddress as Address, addresses as Address[]);
        if (token.standard === "erc721") return evm.getErc721Balances(tokenAddress as Address, addresses as Address[]);
        throw new UnknownTokenStandardError(tokenAddress);
      })();

      // Public RPCs occasionally hang without rejecting; without a timeout the
      // token search spinner never clears even though nothing is progressing.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const balances = await Promise.race([
          fetch,
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error(`Balance fetch timed out for ${tokenAddress} on ${chainTag}`)),
              BALANCE_FETCH_TIMEOUT_MS
            );
          }),
        ]);
        settleClaimedWaiters(waiters, { ok: true, balances });
      } finally {
        if (timeoutId !== undefined) clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error(error);
      settleClaimedWaiters(waiters, { ok: false, error });
    }
  }));
}, 100);


const allBalances: Record<string, Record<ChainTag, Record<string, {
  balance: bigint | bigint[],
  type: "coin" | "nft",
  loading: boolean,
  loaded: boolean,
}>>> = {};


/**
 * Return an 'hydrated' version of the given wallet.
 * Hydrated wallets have a @see balances and @see refreshBalance method
 * use to access and manipulate balance data in a transparent way.
 * @returns
 */
export const hydrateWallet = (
  wallet: Omit<HydratedWallet, "balance" | "balances" | "refreshBalance">
) => {

  const base = {
    ...wallet,
    chainArch: wallet.chainArch ?? "EVM",
  };

  const chains = EVM_CHAINS;

  /**
   * Balances for the wallet, accross all their supported chains.
   * Built in a way that allows for reactive updates and addition of new tokens.
   */
  const balances = allBalances[base.address] ?? chains.reduce((acc, chain) => {
    acc[chain.tag] = {
      [chain.token.address]: reactive({
        balance: 0n,
        type: "coin",
        loading: true,
        loaded: false,
      }),
    };
    return acc;
  }, {} as Record<ChainTag, Record<string, {
    balance: bigint | bigint[],
    type: "coin" | "nft",
    loading: boolean,
    loaded: boolean,
  }>>);

  allBalances[base.address] = balances;


  /**
   * Load the balane for a specific chain and token address.
   * @param chainTag
   * @param tokenAddress
   */
  const refreshBalance = async (chainTag: ChainTag, tokenAddress?: string) => {

    const walletStore = useWalletStore();
    const token = walletStore.token(chainTag, tokenAddress);

    if (!token) {
      console.warn(`No token found for chain ${chainTag} and address ${tokenAddress}. Skipping balance refresh.`);
      return;
    }

    if (!balances[chainTag][token.address]) {
      balances[chainTag][token.address] = reactive({
        balance: token.type === "coin" ? 0n : [],
        type: token.type,
        loading: true,
        loaded: false,
      });
    }

    const entry = balances[chainTag][token.address]!;
    entry.loading = true;

    return new Promise<null>((resolve, reject) => {
      queueBalance(chainTag, token.address, base.address, {
        resolve: (balance) => {
          entry.loading = false;
          entry.loaded  = true;
          entry.balance = token.type === "coin"
            ? balance
            : (balance as bigint[]);
          resolve(null);
        },
        // Keep the last known balance so a transient RPC failure does not
        // render as a real zero.
        reject: (error) => {
          entry.loading = false;
          reject(error);
        },
      });

      processBalanceQueue();
    });
  };


  /**
   * Balance alias for easier access to a given wallet's balance.
   * @param chainTag
   * @param tokenAddress
   * @returns
   */
  const balance = (chainTag: ChainTag, tokenAddress?: string) => {
    const walletStore = useWalletStore();
    const chain = getChain(chainTag);
    if (!tokenAddress) return balances[chainTag][chain.token.address];

    let balance = balances[chainTag][tokenAddress];
    const token = walletStore.token(chainTag, tokenAddress);
    if (token && !balance) {
      balance = reactive({
        balance: token.type === "coin" ? 0n : [],
        type: token.type,
        loading: true,
        loaded: false,
      });
      balances[chainTag][token.address] = balance;
    }

    return balance;
  };


  Object.assign(wallet, base);

  Object.defineProperty(wallet, "refreshBalance", {
    value: refreshBalance,
    writable: true,
    configurable: true,
    enumerable: false,
  });

  Object.defineProperty(wallet, "balances", {
    value: balances,
    writable: true,
    configurable: true,
    enumerable: false,
  });

  Object.defineProperty(wallet, "balance", {
    value: balance,
    writable: true,
    configurable: true,
    enumerable: false,
  });

  return wallet as HydratedWallet;

};


export const hydrateWallets = (wallets: WalletDto[]) => {
  return wallets.map(wallet => hydrateWallet(wallet));
};


/**
 * Will poll the balances of the wallets every X seconds.
 * The polling will stop when the component is unmounted.
 * @param chainTags
 * @param specificWallets
 */
export const useBalancePolling = ({
  chainTags,
  chainTag,
  specificWallets,
  chainArch,
}: {
  chainTags?: ChainTag[] | Ref<ChainTag[]> | ComputedRef<ChainTag[]>,
  chainTag?: ChainTag | Ref<ChainTag> | ComputedRef<ChainTag>,
  specificWallets?: Ref<HydratedWallet[]> | ComputedRef<HydratedWallet[]>
  chainArch?: ChainArch
}) => {

  let isPaused = false;
  let interval: NodeJS.Timeout | null = null;
  let fetchStartedAt: number | null = null;
  const walletStore = useWalletStore();

  // Normalize chainTags to always be an array
  const getChainTags = (): ChainTag[] => {
    if (chainTag) {
      if (isRef(chainTag)) return [chainTag.value];
      return [chainTag];
    }
    if (chainTags) {
      if (isRef(chainTags)) return chainTags.value;
      if (isReactive(chainTags)) return Array.from(chainTags);
      return chainTags;
    }
    throw new Error("No chain tags provided for balance polling.");
  };

  /**
   * Skip a tick while the previous fetch is still running so slow RPCs cannot
   * pile up. The staleness bound means a fetch that never settles delays
   * polling instead of killing it for the rest of the session.
   */
  const fetchNow = async () => {
    if (isPaused) return;
    if (fetchStartedAt !== null && Date.now() - fetchStartedAt < STALE_FETCH_MS) return;
    fetchStartedAt = Date.now();
    try {
      await walletStore.fetchBalances({
        chainTags: getChainTags(),
        explicitWallets: specificWallets?.value,
        chainArch
      });
    } catch (error) {
      console.error(error);
    } finally {
      fetchStartedAt = null;
    }
  };

  const startPolling = async () => {
    if (interval) return;
    interval = setInterval(() => { fetchNow(); }, 5_000); // 5 seconds
  };

  const stopPolling = () => {
    if (interval) {
      clearInterval(interval);
      interval = null;
    }
  };

  // Make the initial fetch
  fetchNow();

  // keepalive pages skip setup() on re-entry; refresh immediately in addition to restarting the interval
  onActivated(() => {
    fetchNow();
    startPolling();
  });
  onMounted(() => { startPolling(); });
  onUnmounted(() => { stopPolling(); });
  onDeactivated(() => { stopPolling(); });

  const pausePolling  = () => { isPaused = true; };
  const resumePolling = () => { isPaused = false; };
  const togglePolling = () => { isPaused = !isPaused; };

  return {
    startPolling,
    stopPolling,
    pausePolling,
    resumePolling,
    togglePolling,
  };
};
